import { SelectOptionProps } from '@src/quince-components/src/atoms/select/Select'
import { ProductOptionDisplayType } from '@src/quince-site-common/src/domains/ProductOption'
import getColorFromUrl from '@src/quince-site-common/src/utils/extractColorFromURL'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'
import { isEqual, mapValues } from '@src/utils/functions/utils'

export interface IFilterConfig {
  readonly columnsToShow: number
  readonly label: string
  readonly type: string
}

export interface IProductTypeFilter {
  readonly title: string
  readonly slug: string
}

export class FilterConfig {
  readonly columnsToShow: number
  readonly label: string
  readonly type: string

  constructor({ columnsToShow, label, type }: IFilterConfig) {
    this.columnsToShow = columnsToShow
    this.label = label
    this.type = type
    return {
      ...this,
    }
  }
}

export interface IFilter {
  readonly name: string
  readonly values: (FilterOptions | string)[]
  readonly config: FilterConfig
}

export interface UpdateQueryParams {
  filter?:
    | { type: 'reset' }
    | { type: 'remove'; name: FilterKeys; value: string }
    | { type: 'toggle'; name: FilterKeys; value: string; label: string }
    | { type: 'removeAll'; name: FilterKeys; label: string; value: string }
    | { type: 'overwrite'; updatedFilters: SelectedFilter }
  sort?: { type: 'reset' } | { type: 'toggle'; value: string }
}

export class Filter implements IFilter {
  readonly name: string
  readonly values: (FilterOptions | string)[]
  readonly config: FilterConfig

  constructor({ name, values, config }: IFilter) {
    this.name = name
    this.values = values
    this.config = config
    return {
      ...this,
    }
  }

  static makeSelectFilterOptionFromValue(value: FilterOptions | string): SelectOptionProps {
    if (typeof value == 'string') {
      return {
        label: value,
        value: value,
      }
    } else {
      return FilterOptions.makeSelectFilterOption(value)
    }
  }
}

export interface IFilterOption {
  readonly name: string
  readonly displayName?: string
  readonly value: string
  readonly displayType?: ProductOptionDisplayType
  readonly hex?: string
  readonly imgURL?: string
}

export class FilterOptions implements IFilterOption {
  readonly name: string
  readonly displayName: string
  readonly value: string
  readonly displayType: ProductOptionDisplayType | undefined
  readonly hex: string | undefined = undefined
  readonly imgURL: string | undefined = undefined

  constructor({ name, value, displayType, displayName, hex = undefined, imgURL = undefined }: IFilterOption) {
    this.name = name
    this.value = value
    this.displayName = displayName ?? name
    this.displayType = displayType
    this.hex = hex
    this.imgURL = imgURL
    return {
      ...this,
    }
  }

  static makeSelectFilterOption(filterOptions: FilterOptions): SelectOptionProps {
    return {
      label: filterOptions.displayName || filterOptions.name,
      value: filterOptions.value || filterOptions.name,
      hex: filterOptions.hex,
      imgURL: filterOptions.imgURL,
    }
  }
}

export type ISelectedFilter = Readonly<Record<string, string[] | undefined>>

export type FilterKeys = keyof ISelectedFilter

// eslint-disable-next-line @typescript-eslint/no-extraneous-class
export class SelectedFilter implements ISelectedFilter {
  [key: string]: string[] | undefined

  constructor(selectedFilter: ISelectedFilter) {
    for (const [key, value] of Object.entries(selectedFilter)) {
      this[key] = value
    }
    return { ...this }
  }

  static isAnyFilterSelected(selectedFilter: SelectedFilter) {
    return !!Object.keys(selectedFilter).find(x =>
      SelectedFilter.isFieldPresent(SelectedFilter.getSelectedFilterValuesByFilterName(selectedFilter, x)),
    )
  }

  private static isFieldPresent(selectedField: string[] | undefined): boolean {
    return !!(selectedField && selectedField.length > 0)
  }

  public static getSelectedValuesAsList(selectedFilter: SelectedFilter): string[] {
    return Object.keys(selectedFilter)
      .map(key => SelectedFilter.getSelectedFilterValuesByFilterName(selectedFilter, key))
      .flat()
  }

  public static getSelectedFilterValuesByFilterName(
    selectedFilter: SelectedFilter,
    filterName: FilterKeys,
    shouldEncode = false,
  ): string[] {
    const selectedFilterList = selectedFilter[filterName] ?? []

    if (shouldEncode) {
      return selectedFilterList.map((filter: string) => encodeURIComponent(filter))
    }

    return selectedFilterList
  }

  public static removeValueFromSelectedFilter(selectedFilter: SelectedFilter, value: string, filterName?: FilterKeys) {
    if (filterName) {
      const currentFilterValues = SelectedFilter.getSelectedFilterValuesByFilterName(selectedFilter, filterName)
      const updatedFilterValues = currentFilterValues.filter(item => item !== value)
      return new SelectedFilter({
        ...selectedFilter,
        [filterName]: updatedFilterValues,
      })
    } else {
      const sanitizedFilter = mapValues(selectedFilter, (arr?: string[]) => {
        const filteredArray = arr?.filter(x => x !== value)
        return filteredArray?.length === 0 ? undefined : filteredArray
      })
      return new SelectedFilter(sanitizedFilter)
    }
  }

  public static removeAllValuesFromSelectedFilter(selectedFilter: SelectedFilter, filterName: FilterKeys) {
    return new SelectedFilter({
      ...selectedFilter,
      [filterName]: [],
    })
  }

  public static isFilterValueSelected(selectedFilter: SelectedFilter, filterName: FilterKeys, value: string) {
    const currentFilterValues = SelectedFilter.getSelectedFilterValuesByFilterName(selectedFilter, filterName)
    return currentFilterValues.includes(value)
  }

  public static addValueToSelectedFilter(selectedFilter: SelectedFilter, filterName: FilterKeys, value: string) {
    const updatedFilterValues = SelectedFilter.isSingleSelectFilter(filterName)
      ? [value]
      : [...SelectedFilter.getSelectedFilterValuesByFilterName(selectedFilter, filterName), value]

    return new SelectedFilter({
      ...selectedFilter,
      [filterName]: updatedFilterValues,
    })
  }

  public static toggleValueFromSelectedFilter(selectedFilter: SelectedFilter, filterName: FilterKeys, value: string) {
    const currentFilterValues = SelectedFilter.getSelectedFilterValuesByFilterName(selectedFilter, filterName)
    if (currentFilterValues.includes(value)) {
      trackEvent(TELEMETRY_EVENT_NAMES.INDIVIDUAL_FILTERS_CLEARED, {
        filter_key: filterName,
        filter_value: value,
      })
      return SelectedFilter.removeValueFromSelectedFilter(selectedFilter, value, filterName)
    } else {
      return SelectedFilter.addValueToSelectedFilter(selectedFilter, filterName, value)
    }
  }

  public static getUpdatedSelectedFilterStringForWindowURL(selectedFilter: SelectedFilter) {
    const filterString = Object.keys(selectedFilter)
      .map(key => {
        const values = SelectedFilter.getSelectedFilterValuesByFilterName(selectedFilter, key, true)
        if (values.length > 0) {
          return `${key}=${values.join(',')}`
        }
        return undefined
      })
      .filter(value => value !== undefined)
      .join('&')

    return filterString
  }

  public static isSingleSelectFilter(filterName: FilterKeys): boolean {
    return filterName === String(EFilterKeys.PRODUCT_TYPE) || filterName === String(EFilterKeys.SUBCOLLECTIONS)
  }

  public static getSelectedFilterFromQueryParams(filter: string): SelectedFilter {
    const filterValues = {} as Record<FilterKeys, string[] | undefined>

    // Manually parse the filter string to handle encoded commas correctly
    // queryString.parse decodes values automatically, which breaks filter values containing commas
    const pairs = filter.split('&')
    pairs.forEach(pair => {
      const equalIndex = pair.indexOf('=')
      if (equalIndex > 0) {
        const key = pair.substring(0, equalIndex)
        const encodedValues = pair.substring(equalIndex + 1)

        // Split by comma (our delimiter) BEFORE decoding
        // This preserves encoded commas (%2C) within individual filter values
        const values = encodedValues
          .split(',')
          .map(v => decodeURIComponent(v.trim()))
          .filter(v => v.length > 0)

        if (values.length > 0) {
          filterValues[key] = values
        }
      }
    })

    return new SelectedFilter(filterValues)
  }

  public static checkEquality(left: SelectedFilter, right: SelectedFilter): boolean {
    return isEqual<SelectedFilter>(left, right)
  }

  public static getColorFromUrl(): string {
    const {
      location: { pathname },
    } = window
    return getColorFromUrl(pathname)
  }

  /**
   * Creates a new SelectedFilter instance from scratch, containing only a single
   * subcollections value. This effectively resets all other filters.
   * @param productTypeValue The value for the subcollections filter.
   * @returns A new SelectedFilter instance.
   */
  public static initializeWithProductType(productTypeValue: string): SelectedFilter {
    return SelectedFilter.addValueToSelectedFilter(new SelectedFilter({}), EFilterKeys.SUBCOLLECTIONS, productTypeValue)
  }
}

export enum EFilterKeys {
  COLORS = 'colors',
  SIZES = 'sizes',
  PRICES = 'prices',
  MATERIALS = 'materials',
  STYLES = 'styles',
  CONSTRUCTIONS = 'constructions',
  FABRICS = 'fabrics',
  PILE_HEIGHT = 'pile_height',
  CATEGORY_SWEATER = 'category_sweater',
  CATEGORIES = 'categories',
  DEPARTMENTS = 'departments',
  STONE_SHAPES = 'stone_shapes',
  DIAMOND_TYPES = 'diamond_types',
  JACKET_STYLES = 'jacket_styles',
  LENGTHS = 'lengths',
  WARMTHS = 'warmths',
  SUBCOLLECTIONS = 'subcollections',
  PRODUCT_TYPE = 'productType',
  ACTIVITY = 'activity',
  SLEEVE = 'sleeves',
  OCCASION = 'occasions',
}
